import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../libraries/clipboardUtils";
import {
  compressImageDataUrl,
  DUTY_HOURS_LABEL,
  EXPECTED_WORK_MINUTES,
  fetchAttendanceRecords,
  fetchHolidayRecords,
  findEmployeeRecord,
  formatClockTime,
  formatDuration,
  getCheckInMarkTime,
  getNetDeficitMinutes,
  getCurrentEmployee,
  getDaysInMonth,
  getEmployeeLoginId,
  getEmployeeName,
  getEmployeeRecordId,
  getHolidayForDate,
  getLocalDateKey,
  isEarlyCheckout,
  getMonthLabel,
  getTodayAttendanceState,
  saveAttendanceRecord,
  saveHolidayRecord,
  withAttendanceTotals
} from "../../libraries/tecgymAdminAttendance";

function getEmployeeRowKey(employee) {
  return getEmployeeRecordId(employee) || getEmployeeLoginId(employee) || getEmployeeName(employee);
}

function getEmployeeInitials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function getEmployeeRole(employee) {
  if (!employee) {
    return "";
  }

  if (typeof employee.designation === "string" && employee.designation.trim()) {
    return employee.designation.trim();
  }

  if (typeof employee.role === "string") {
    return employee.role.trim();
  }

  if (employee.role && typeof employee.role === "object") {
    return employee.role.name || employee.role.roleName || employee.role.title || "";
  }

  return employee.roleName || employee.userRole || "";
}

const ROLE_DISPLAY_ORDER = [
  { key: "admin", aliases: ["admin", "administrator"] },
  { key: "developer", aliases: ["developer"] }
];

function getRoleSortIndex(roleName) {
  const roleKey = String(roleName || "").trim().toLowerCase();

  for (let index = 0; index < ROLE_DISPLAY_ORDER.length; index += 1) {
    if (ROLE_DISPLAY_ORDER[index].aliases.includes(roleKey)) {
      return index;
    }
  }

  return ROLE_DISPLAY_ORDER.length;
}

function sortEmployeesByRole(employees) {
  return [...employees].sort((employeeA, employeeB) => {
    const roleDiff = getRoleSortIndex(getEmployeeRole(employeeA)) - getRoleSortIndex(getEmployeeRole(employeeB));

    if (roleDiff !== 0) {
      return roleDiff;
    }

    return getEmployeeName(employeeA).localeCompare(getEmployeeName(employeeB));
  });
}

async function requestAttendanceCameraStream() {
  const constraintAttempts = [
    { video: { facingMode: "user" }, audio: false },
    { video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
    { video: true, audio: false }
  ];

  let lastError = null;

  for (const constraints of constraintAttempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to access the camera.");
}

function getCameraErrorMessage(error) {
  const message = String(error?.message || error || "").toLowerCase();

  if (message.includes("permission") || message.includes("notallowed")) {
    return "Camera permission denied. Please allow camera access in your browser settings.";
  }

  if (message.includes("notfound") || message.includes("devicesnotfound")) {
    return "No camera found on this device.";
  }

  if (message.includes("notreadable") || message.includes("could not start video source") || message.includes("in use")) {
    return "Camera is busy or unavailable. Close other apps using the camera and try again.";
  }

  return error?.message || "Unable to open the laptop camera. Please allow camera access.";
}

async function attachStreamToVideo(videoEl, stream) {
  if (!videoEl || !stream) {
    return;
  }

  videoEl.srcObject = stream;

  await new Promise((resolve, reject) => {
    const cleanup = () => {
      videoEl.removeEventListener("loadedmetadata", onReady);
      videoEl.removeEventListener("error", onError);
    };

    const onReady = () => {
      cleanup();
      resolve();
    };

    const onError = () => {
      cleanup();
      reject(new Error("Could not start video source."));
    };

    if (videoEl.readyState >= 1) {
      resolve();
      return;
    }

    videoEl.addEventListener("loadedmetadata", onReady, { once: true });
    videoEl.addEventListener("error", onError, { once: true });
  });

  try {
    await videoEl.play();
  } catch (error) {
    if (videoEl.readyState < 2) {
      throw error;
    }
  }
}

function AttendanceTab({ users = [], usersLoading = false, isAdminUser = false, loggedInUser }) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraMode, setCameraMode] = useState("check-in");
  const [previewImage, setPreviewImage] = useState("");
  const [holidays, setHolidays] = useState(() => fetchHolidayRecords());
  const [isHolidayPopupOpen, setIsHolidayPopupOpen] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ date: getLocalDateKey(), occasion: "" });
  const [holidayError, setHolidayError] = useState("");
  const [holidaySaving, setHolidaySaving] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [earlyCheckoutOpen, setEarlyCheckoutOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraStartTokenRef = useRef(0);

  const currentEmployee = useMemo(() => getCurrentEmployee() || loggedInUser, [loggedInUser]);
  const allEmployees = useMemo(() => {
    const list = users.length > 0
      ? users
      : (currentEmployee ? [currentEmployee] : []);

    return sortEmployeesByRole(list);
  }, [currentEmployee, users]);
  const [selectedEmployeeKey, setSelectedEmployeeKey] = useState(() => (
    isAdminUser ? "" : (getEmployeeRowKey(currentEmployee) || "")
  ));

  useEffect(() => {
    if (isAdminUser) {
      if (!selectedEmployeeKey) {
        return;
      }

      const stillValid = allEmployees.some((employee) => getEmployeeRowKey(employee) === selectedEmployeeKey);
      if (!stillValid) {
        setSelectedEmployeeKey("");
      }
      return;
    }

    const ownKey = getEmployeeRowKey(currentEmployee);
    if (ownKey && selectedEmployeeKey !== ownKey) {
      setSelectedEmployeeKey(ownKey);
    }
  }, [allEmployees, currentEmployee, isAdminUser, selectedEmployeeKey]);

  const selectedEmployee = useMemo(() => {
    if (!selectedEmployeeKey) {
      return isAdminUser ? null : (currentEmployee || null);
    }

    return allEmployees.find((employee) => getEmployeeRowKey(employee) === selectedEmployeeKey)
      || (!isAdminUser ? currentEmployee : null)
      || null;
  }, [allEmployees, currentEmployee, isAdminUser, selectedEmployeeKey]);

  const showAttendanceTable = Boolean(selectedEmployee) && (!isAdminUser || Boolean(selectedEmployeeKey));
  const selectedEmployeeName = selectedEmployee ? getEmployeeName(selectedEmployee) : "Employee";
  const canMarkForSelected = Boolean(
    selectedEmployee
    && getEmployeeRowKey(selectedEmployee) === getEmployeeRowKey(currentEmployee)
  );

  const monthDays = useMemo(() => getDaysInMonth(viewYear, viewMonth), [viewMonth, viewYear]);
  const todayKey = getLocalDateKey();
  const todayState = useMemo(
    () => getTodayAttendanceState(records, selectedEmployee || currentEmployee),
    [currentEmployee, records, selectedEmployee]
  );

  const attendanceEmployee = selectedEmployee || (!isAdminUser ? currentEmployee : null);

  const loadRecords = useCallback(async () => {
    if (!attendanceEmployee) {
      setRecords([]);
      return;
    }

    setRecords([]);
    setLoading(true);
    try {
      const nextRecords = await fetchAttendanceRecords(viewMonth + 1, viewYear, attendanceEmployee);
      setRecords(nextRecords);
    } catch (error) {
      showToast(error.message || "Failed to load attendance.", "error");
    } finally {
      setLoading(false);
    }
  }, [attendanceEmployee, viewMonth, viewYear]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const stopCamera = useCallback(() => {
    cameraStartTokenRef.current += 1;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const closeCamera = useCallback(() => {
    stopCamera();
    setIsCameraOpen(false);
    setCameraError("");
    setCameraStarting(false);
    setEarlyCheckoutOpen(false);
  }, [stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const bindCameraStream = useCallback(async (videoEl) => {
    const stream = streamRef.current;
    if (!videoEl || !stream) {
      return;
    }

    try {
      await attachStreamToVideo(videoEl, stream);
      setCameraError("");
    } catch (error) {
      setCameraError(getCameraErrorMessage(error));
    }
  }, []);

  const startCamera = useCallback(async () => {
    const startToken = cameraStartTokenRef.current + 1;
    cameraStartTokenRef.current = startToken;
    setCameraStarting(true);
    setCameraError("");

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera is not available in this browser. Please use a laptop with a camera.");
      setCameraStarting(false);
      return;
    }

    stopCamera();
    cameraStartTokenRef.current = startToken;

    try {
      const stream = await requestAttendanceCameraStream();

      if (cameraStartTokenRef.current !== startToken) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        await bindCameraStream(videoRef.current);
      }
    } catch (error) {
      if (cameraStartTokenRef.current === startToken) {
        setCameraError(getCameraErrorMessage(error));
      }
    } finally {
      if (cameraStartTokenRef.current === startToken) {
        setCameraStarting(false);
      }
    }
  }, [bindCameraStream, stopCamera]);

  const handleVideoRef = useCallback((node) => {
    videoRef.current = node;

    if (node && streamRef.current) {
      bindCameraStream(node);
    }
  }, [bindCameraStream]);

  useEffect(() => {
    if (!isCameraOpen) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      startCamera();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isCameraOpen, startCamera]);

  const handleOpenMarkAttendance = () => {
    if (!currentEmployee) {
      showToast("Unable to identify the logged-in employee.", "error");
      return;
    }

    if (!canMarkForSelected) {
      showToast("Select your own name to mark attendance.", "info");
      return;
    }

    const today = new Date();
    if (today.getDay() === 0 || today.getDay() === 6) {
      showToast("Weekend — office closed. Attendance cannot be marked.", "info");
      return;
    }

    if (getHolidayForDate(holidays, getLocalDateKey(today))) {
      showToast("Holiday — office closed. Attendance cannot be marked.", "info");
      return;
    }

    if (todayState.mode === "complete") {
      showToast("Today's check-in and check-out are already marked.", "info");
      return;
    }

    setCameraMode(todayState.mode);
    setIsCameraOpen(true);
  };

  const handleCaptureAndSave = async () => {
    if (cameraMode === "check-out" && isEarlyCheckout(new Date())) {
      setEarlyCheckoutOpen(true);
      return;
    }

    await completeCheckoutCapture();
  };

  const confirmEarlyCheckout = () => {
    setEarlyCheckoutOpen(false);
    completeCheckoutCapture();
  };

  const completeCheckoutCapture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setCameraError("Camera is not ready yet. Please wait a moment and try again.");
      return;
    }

    try {
      setSaving(true);
      setCameraError("");

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Unable to capture the camera image.");
      }
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const image = await compressImageDataUrl(canvas.toDataURL("image/jpeg", 0.8));
      const nowTime = new Date();
      const existing = findEmployeeRecord(records, currentEmployee, getLocalDateKey(nowTime));

      let nextRecord;
      if (cameraMode === "check-in") {
        const checkInTime = getCheckInMarkTime(nowTime);
        nextRecord = withAttendanceTotals({
          id: existing?.id,
          dateKey: getLocalDateKey(nowTime),
          employeeId: getEmployeeRecordId(currentEmployee),
          employeeName: getEmployeeName(currentEmployee),
          employeeEmail: getEmployeeLoginId(currentEmployee),
          checkInTime: checkInTime.toISOString(),
          checkInImage: image,
          checkOutTime: "",
          checkOutImage: "",
          createdAt: nowTime.toISOString()
        });
      } else {
        if (!existing?.checkInTime) {
          throw new Error("Please mark check-in first.");
        }
        nextRecord = withAttendanceTotals({
          ...existing,
          employeeId: existing?.employeeId || getEmployeeRecordId(currentEmployee),
          employeeName: existing?.employeeName || getEmployeeName(currentEmployee),
          employeeEmail: existing?.employeeEmail || getEmployeeLoginId(currentEmployee),
          dateKey: existing?.dateKey || getLocalDateKey(nowTime),
          checkOutTime: nowTime.toISOString(),
          checkOutImage: image
        });
      }

      const saved = await saveAttendanceRecord(nextRecord, cameraMode === "check-out" && Boolean(existing?.id));
      setRecords((current) => {
        const withoutCurrent = current.filter((item) => item.id !== saved.id);
        const matchIndex = withoutCurrent.findIndex((item) =>
          item.dateKey === saved.dateKey
          && (
            (saved.employeeId && item.employeeId === saved.employeeId)
            || (saved.employeeEmail && item.employeeEmail === saved.employeeEmail)
          )
        );
        if (matchIndex >= 0) {
          const next = [...withoutCurrent];
          next[matchIndex] = saved;
          return next;
        }
        return [...withoutCurrent, saved];
      });

      closeCamera();
      showToast(
        cameraMode === "check-in"
          ? `Check-in marked at ${formatClockTime(saved.checkInTime)}.`
          : `Check-out marked at ${formatClockTime(saved.checkOutTime)}${saved.isEarlyCheckout ? ". Early checkout" : ""}${saved.overtimeMinutes > 0 ? `. Overtime ${formatDuration(saved.overtimeMinutes)}` : ""}.`,
        "success"
      );
    } catch (error) {
      setCameraError(error.message || "Unable to mark attendance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const changeMonth = (offset) => {
    const next = new Date(viewYear, viewMonth + offset, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const openHolidayPopup = () => {
    setHolidayError("");
    setHolidayForm({ date: getLocalDateKey(), occasion: "" });
    setIsHolidayPopupOpen(true);
  };

  const closeHolidayPopup = () => {
    setIsHolidayPopupOpen(false);
    setHolidayError("");
    setHolidaySaving(false);
  };

  const handleSaveHoliday = () => {
    try {
      setHolidaySaving(true);
      setHolidayError("");
      const saved = saveHolidayRecord({
        dateKey: holidayForm.date,
        occasion: holidayForm.occasion
      });
      setHolidays(fetchHolidayRecords());
      closeHolidayPopup();
      showToast(`Holiday marked for ${saved.dateKey}: ${saved.occasion}`, "success");
    } catch (error) {
      setHolidayError(error.message || "Unable to save holiday.");
    } finally {
      setHolidaySaving(false);
    }
  };

  const tableRows = monthDays.map((day) => {
    const weekday = day.date.getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    const holiday = getHolidayForDate(holidays, day.dateKey);
    const isClosed = isWeekend || Boolean(holiday);
    const record = selectedEmployee && !isClosed
      ? findEmployeeRecord(records, selectedEmployee, day.dateKey)
      : null;

    return {
      key: day.dateKey,
      day,
      isWeekend,
      holiday,
      isClosed,
      record
    };
  });

  const monthOverviewRows = monthDays.map((day) => {
    const weekday = day.date.getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    const holiday = getHolidayForDate(holidays, day.dateKey);

    return {
      key: day.dateKey,
      day,
      isWeekend,
      holiday
    };
  });
  const leadingBlankDays = monthOverviewRows.length
    ? (monthOverviewRows[0].day.date.getDay() + 6) % 7
    : 0;

  const monthSummary = useMemo(() => {
    let weekends = 0;
    let holidaysCount = 0;
    let workingDays = 0;
    let workedMinutes = 0;
    let deficitMinutes = 0;
    let overtimeMinutes = 0;

    tableRows.forEach((row) => {
      if (row.isWeekend) {
        weekends += 1;
      }

      if (row.holiday) {
        holidaysCount += 1;
      }

      if (!row.isClosed) {
        workingDays += 1;
        workedMinutes += Number(row.record?.workingMinutes) || 0;
        deficitMinutes += Number(row.record?.deficitMinutes) || 0;
        overtimeMinutes += Number(row.record?.overtimeMinutes) || 0;
      }
    });

    return {
      totalDays: tableRows.length,
      workingDays,
      totalWorkingMinutes: workingDays * EXPECTED_WORK_MINUTES,
      workedMinutes,
      deficitMinutes: getNetDeficitMinutes(deficitMinutes, overtimeMinutes),
      overtimeMinutes,
      holidays: holidaysCount,
      weekends
    };
  }, [tableRows]);

  const punchedAt = new Date();
  const recordedTime = cameraMode === "check-in" ? getCheckInMarkTime(punchedAt) : punchedAt;
  const checkInAdjusted = cameraMode === "check-in" && recordedTime.getTime() !== punchedAt.getTime();
  const markDisabled = saving || todayState.mode === "complete" || !canMarkForSelected;

  return (
    <section style={sectionStyle}>
      <div className="attendance-toolbar" style={sectionHeaderStyle}>
        <div style={headerLeftStyle}>
          <div style={titleRowStyle}>
            {showAttendanceTable && (
              <h2 style={employeeNameAboveStyle}>{selectedEmployeeName}</h2>
            )}
            <h2 style={sectionTitleStyle}>Attendance</h2>
          </div>
          <p style={showAttendanceTable ? dutySubtitleStyle : sectionSubtitleStyle}>
            {showAttendanceTable ? (
              <span style={dutyTimingTextStyle}>Duty timing {DUTY_HOURS_LABEL}.</span>
            ) : (
              "Select an employee to view their monthly attendance, or add holidays below."
            )}
          </p>
        </div>

        {(isAdminUser && !showAttendanceTable) || showAttendanceTable ? (
          <div className="attendance-month-switch" style={headerMonthNavStyle}>
            <button type="button" className="attendance-month-btn" onClick={() => changeMonth(-1)}>
              Previous
            </button>
            <strong className="attendance-month-label" style={monthLabelStyle}>{getMonthLabel(viewYear, viewMonth)}</strong>
            <button type="button" className="attendance-month-btn" onClick={() => changeMonth(1)}>
              Next
            </button>
          </div>
        ) : null}

        <div style={headerActionsStyle}>
          {isAdminUser && !showAttendanceTable && (
            <button type="button" style={addUserButtonStyle} onClick={openHolidayPopup}>
              Add Holiday
            </button>
          )}
          {isAdminUser && showAttendanceTable && (
            <button type="button" style={secondaryButtonStyle} onClick={() => setSelectedEmployeeKey("")}>
              Back
            </button>
          )}
          {showAttendanceTable && (
            <button
              type="button"
              style={markDisabled ? disabledButtonStyle : addUserButtonStyle}
              onClick={handleOpenMarkAttendance}
              disabled={markDisabled}
            >
              {todayState.mode === "complete" ? "Attendance Marked" : "Mark Attendance"}
            </button>
          )}
        </div>
      </div>

      {!showAttendanceTable ? (
        usersLoading ? (
          <div style={emptyStateStyle}>Loading employees...</div>
        ) : (
          <>
            {allEmployees.length === 0 ? (
              <div style={emptyStateStyle}>No employees found for attendance.</div>
            ) : (
              <div className="attendance-people">
                <div className="attendance-people-label">Employees</div>
                <div className="attendance-people-grid">
                  {allEmployees.map((employee) => {
                    const name = getEmployeeName(employee);

                    return (
                      <button
                        key={getEmployeeRowKey(employee)}
                        type="button"
                        className="attendance-employee-card"
                        onClick={() => setSelectedEmployeeKey(getEmployeeRowKey(employee))}
                        title={name}
                      >
                        <span className="attendance-employee-avatar" aria-hidden="true">{getEmployeeInitials(name)}</span>
                        <span className="attendance-employee-name">{name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isAdminUser && (
              <section className="attendance-calendar" aria-label="Month calendar">
                <div className="attendance-calendar-legend">
                  <span><i className="working" /> Working day</span>
                  <span><i className="weekend" /> Weekend</span>
                  <span><i className="holiday" /> Holiday</span>
                  <span><i className="today" /> Today</span>
                </div>
                <div className="attendance-calendar-weekdays">
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((name) => (
                    <div key={name} className={name === "Saturday" || name === "Sunday" ? "is-weekend" : ""}>
                      {name.slice(0, 3)}
                    </div>
                  ))}
                </div>
                <div className="attendance-calendar-grid">
                  {Array.from({ length: leadingBlankDays }, (_, index) => (
                    <div key={`blank-${index}`} className="attendance-day-blank" aria-hidden="true" />
                  ))}
                  {monthOverviewRows.map((row) => {
                    const isToday = row.day.dateKey === todayKey;
                    const kind = row.holiday ? "holiday" : row.isWeekend ? "weekend" : "working";
                    const centerLabel = row.holiday
                      ? row.holiday.occasion
                      : row.isWeekend
                        ? "Weekend"
                        : "Working day";

                    return (
                      <article
                        key={row.key}
                        className={`attendance-day-card is-${kind}${isToday ? " is-today" : ""}`}
                      >
                        <div className="attendance-day-top">
                          <span className="attendance-day-name">{row.day.dayName}</span>
                          <span className="attendance-day-date">{row.day.dateLabel.replace(/^0/, "")}</span>
                        </div>
                        <div className="attendance-day-body">
                          <span className="attendance-day-pill">
                            {row.holiday ? "Holiday" : centerLabel}
                          </span>
                          {row.holiday && <span className="attendance-day-note">{row.holiday.occasion}</span>}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )
      ) : (
        <>
          {loading ? (
            <div style={emptyStateStyle}>Loading attendance...</div>
          ) : (
            <>
              <div className="attendance-register">
                <table className="attendance-register-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Day</th>
                      <th>Check in Time</th>
                      <th>Check in image</th>
                      <th>Checkout time</th>
                      <th>Check out image</th>
                      <th>Working hrs</th>
                      <th>Deficit hrs</th>
                      <th>Overtime</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, index) => {
                      const isToday = row.day.dateKey === todayKey;
                      const isLate = Boolean(row.record?.isLate);
                      const checkedOutEarly = Boolean(row.record?.isEarlyCheckout);
                      const kind = row.holiday ? "holiday" : row.isWeekend ? "weekend" : "working";
                      const checkInLabel = formatClockTime(row.record?.checkInTime);
                      const checkOutLabel = formatClockTime(row.record?.checkOutTime);
                      const workingLabel = formatDuration(row.record?.workingMinutes);
                      const deficitLabel = formatDuration(row.record?.deficitMinutes);
                      const overtimeMinutes = Number(row.record?.overtimeMinutes) || 0;
                      const overtimeLabel = overtimeMinutes > 0 ? formatDuration(overtimeMinutes) : "—";

                      return (
                        <tr
                          key={row.key}
                          className={`is-${kind}${isToday ? " is-today" : ""}${!row.isClosed && index % 2 === 1 ? " is-alt" : ""}`}
                        >
                          <td className="attendance-register-date">{row.day.dateLabel.replace(/^0/, "")}</td>
                          <td className="attendance-register-day">{row.day.dayName}</td>
                          {row.isClosed ? (
                            <td colSpan={7} className="attendance-register-closed">
                              <span className="attendance-register-pill">
                                {row.holiday ? "Holiday" : "Weekend"}
                              </span>
                              {row.holiday ? <span className="attendance-register-note">{row.holiday.occasion}</span> : null}
                            </td>
                          ) : (
                            <>
                              <td className="is-nowrap">
                                <span className={checkInLabel === "—" ? "is-empty" : ""}>{checkInLabel}</span>
                                {isLate ? <span style={attendanceMarkStyle}> Late</span> : ""}
                              </td>
                              <td>
                                <AttendanceImage src={row.record?.checkInImage} alt="Check in" onOpen={setPreviewImage} />
                              </td>
                              <td className="is-nowrap">
                                <span className={checkOutLabel === "—" ? "is-empty" : ""}>{checkOutLabel}</span>
                                {checkedOutEarly ? <span style={attendanceMarkStyle}> Early checkout</span> : ""}
                              </td>
                              <td>
                                <AttendanceImage src={row.record?.checkOutImage} alt="Check out" onOpen={setPreviewImage} />
                              </td>
                              <td><span className={workingLabel === "—" ? "is-empty" : ""}>{workingLabel}</span></td>
                              <td><span className={deficitLabel === "—" ? "is-empty" : ""}>{deficitLabel}</span></td>
                              <td>
                                <span className={overtimeMinutes > 0 ? "is-overtime" : "is-empty"}>{overtimeLabel}</span>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="attendance-summary">
                <h3>Summary</h3>
                <div className="attendance-summary-grid">
                  <div><span>Total Days</span><strong>{monthSummary.totalDays}</strong></div>
                  <div><span>Total Working Days</span><strong>{monthSummary.workingDays}</strong></div>
                  <div><span>Total Working Hrs</span><strong>{formatDuration(monthSummary.totalWorkingMinutes)}</strong></div>
                  <div><span>Worked Hrs</span><strong>{formatDuration(monthSummary.workedMinutes)}</strong></div>
                  <div><span>Deficit Hrs</span><strong>{formatDuration(monthSummary.deficitMinutes)}</strong></div>
                  <div><span>Total Holidays</span><strong>{monthSummary.holidays}</strong></div>
                  <div><span>Weekends</span><strong>{monthSummary.weekends}</strong></div>
                  <div><span>Overtime Hrs</span><strong className="is-overtime">{formatDuration(monthSummary.overtimeMinutes)}</strong></div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {isHolidayPopupOpen && (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Add holiday">
          <div style={modalCardStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>Add Holiday</h2>
                <p style={modalSubtitleStyle}>Select a date and enter the holiday occasion.</p>
              </div>
              <button type="button" style={closeButtonStyle} onClick={closeHolidayPopup} aria-label="Close holiday popup">
                X
              </button>
            </div>

            <label style={formLabelStyle}>
              Date
              <input
                type="date"
                value={holidayForm.date}
                onChange={(event) => setHolidayForm((current) => ({ ...current, date: event.target.value }))}
                style={formInputStyle}
              />
            </label>

            <label style={formLabelStyle}>
              Holiday Occasion
              <input
                type="text"
                value={holidayForm.occasion}
                onChange={(event) => setHolidayForm((current) => ({ ...current, occasion: event.target.value }))}
                placeholder="e.g. Independence Day"
                style={formInputStyle}
              />
            </label>

            {holidayError && <div style={formErrorStyle}>{holidayError}</div>}

            <div style={modalActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={closeHolidayPopup} disabled={holidaySaving}>
                Cancel
              </button>
              <button type="button" style={addUserButtonStyle} onClick={handleSaveHoliday} disabled={holidaySaving}>
                {holidaySaving ? "Saving..." : "Save Holiday"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCameraOpen && (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-label="Mark attendance camera">
          <div style={modalCardStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>{cameraMode === "check-in" ? "Check In" : "Check Out"}</h2>
                <p style={modalSubtitleStyle}>
                  {checkInAdjusted
                    ? `Laptop time minus 10 minutes: ${formatClockTime(recordedTime)}`
                    : `Exact laptop time: ${formatClockTime(recordedTime)}`}
                </p>
              </div>
              <button type="button" style={closeButtonStyle} onClick={closeCamera} aria-label="Close camera">
                X
              </button>
            </div>

            <div style={cameraFrameStyle}>
              {cameraStarting && !cameraError && (
                <div style={cameraLoadingStyle}>Starting camera...</div>
              )}
              <video
                ref={handleVideoRef}
                autoPlay
                playsInline
                muted
                style={cameraVideoStyle}
              />
            </div>

            {cameraError && <div style={formErrorStyle}>{cameraError}</div>}

            <div style={modalActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={closeCamera} disabled={saving}>
                Cancel
              </button>
              {cameraError ? (
                <button type="button" style={addUserButtonStyle} onClick={startCamera} disabled={cameraStarting}>
                  {cameraStarting ? "Retrying..." : "Retry Camera"}
                </button>
              ) : null}
              <button
                type="button"
                style={addUserButtonStyle}
                onClick={handleCaptureAndSave}
                disabled={saving || cameraStarting || Boolean(cameraError) || earlyCheckoutOpen}
              >
                {saving ? "Saving..." : cameraMode === "check-in" ? "Capture & Check In" : "Capture & Check Out"}
              </button>
            </div>
          </div>
        </div>
      )}

      {earlyCheckoutOpen && (
        <div style={earlyCheckoutOverlayStyle} role="dialog" aria-modal="true" aria-label="Early check out">
          <div style={earlyCheckoutCardStyle}>
            <h2 style={modalTitleStyle}>Check out early?</h2>
            <p style={earlyCheckoutMessageStyle}>Are you sure you want to check out early?</p>
            <div style={modalActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={() => setEarlyCheckoutOpen(false)}>
                No
              </button>
              <button type="button" style={addUserButtonStyle} onClick={confirmEarlyCheckout}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Attendance photo"
          onClick={() => setPreviewImage("")}
          onContextMenu={blockCapturedImageSave}
        >
          <ProtectedCapturedImage src={previewImage} alt="Attendance" style={previewImageStyle} />
        </div>
      )}

      <style>{`
        .attendance-month-switch {
          padding: 5px;
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .attendance-month-btn {
          border: none;
          background: transparent;
          color: #e5e7eb;
          border-radius: 12px;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
        }
        .attendance-month-btn:hover {
          background: rgba(255, 255, 255, 0.08);
        }
        .attendance-month-label {
          min-width: 132px;
          text-align: center;
          letter-spacing: 0.02em;
        }
        .attendance-people {
          margin-bottom: 18px;
          padding: 16px;
          border-radius: 22px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          background:
            radial-gradient(700px 180px at 100% 0%, rgba(24, 119, 24, 0.16), transparent 55%),
            linear-gradient(180deg, #1b2029 0%, #14181f 100%);
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
        }
        .attendance-people-label {
          margin-bottom: 12px;
          color: #9ca3af;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .attendance-people-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 10px;
        }
        .attendance-employee-card {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          min-width: 0;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 14px;
          padding: 8px 12px 8px 8px;
          background: rgba(255, 255, 255, 0.03);
          color: #fff;
          cursor: pointer;
          text-align: left;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
          transition: transform 0.18s ease, border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
        }
        .attendance-employee-card:hover {
          transform: translateY(-2px);
          border-color: rgba(34, 197, 94, 0.7);
          background: rgba(24, 119, 24, 0.16);
          box-shadow: 0 8px 18px rgba(24, 119, 24, 0.22);
        }
        .attendance-employee-card:active {
          transform: translateY(0);
        }
        .attendance-employee-avatar {
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #187718 0%, #22c55e 100%);
          color: #fff;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.04em;
        }
        .attendance-employee-name {
          min-width: 0;
          font-size: 13px;
          font-weight: 800;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .attendance-calendar {
          margin-top: 4px;
          padding: 18px;
          border-radius: 22px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          background:
            radial-gradient(900px 220px at 0% 0%, rgba(24, 119, 24, 0.18), transparent 60%),
            linear-gradient(180deg, #1b2029 0%, #14181f 100%);
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
        }
        .attendance-calendar-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 12px 18px;
          margin-bottom: 16px;
          color: #c5cad3;
          font-size: 12px;
          font-weight: 700;
        }
        .attendance-calendar-legend span {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .attendance-calendar-legend i {
          width: 8px;
          height: 8px;
          border-radius: 99px;
          display: inline-block;
        }
        .attendance-calendar-legend i.working { background: #4ade80; box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.16); }
        .attendance-calendar-legend i.weekend { background: #fb7185; box-shadow: 0 0 0 3px rgba(251, 113, 133, 0.16); }
        .attendance-calendar-legend i.holiday { background: #60a5fa; box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.16); }
        .attendance-calendar-legend i.today { background: transparent; box-shadow: 0 0 0 2px #22c55e; }
        .attendance-calendar-weekdays,
        .attendance-calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 10px;
        }
        .attendance-calendar-weekdays { margin-bottom: 8px; }
        .attendance-calendar-weekdays div {
          text-align: center;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #9ca3af;
        }
        .attendance-calendar-weekdays .is-weekend { color: #fb7185; }
        .attendance-day-blank {
          min-height: 118px;
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px dashed rgba(255, 255, 255, 0.06);
        }
        .attendance-day-card {
          position: relative;
          min-height: 118px;
          border-radius: 16px;
          padding: 12px;
          display: flex;
          flex-direction: column;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.02));
          border: 1px solid rgba(255, 255, 255, 0.07);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
          overflow: hidden;
          transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
        }
        .attendance-day-card::before {
          content: "";
          position: absolute;
          left: 14px;
          right: 14px;
          top: 0;
          height: 2px;
          border-radius: 0 0 6px 6px;
          background: #3f4654;
        }
        .attendance-day-card.is-working::before { background: #22c55e; }
        .attendance-day-card.is-weekend { background: linear-gradient(180deg, rgba(251, 113, 133, 0.14), rgba(251, 113, 133, 0.04)); }
        .attendance-day-card.is-weekend::before { background: #fb7185; }
        .attendance-day-card.is-holiday { background: linear-gradient(180deg, rgba(96, 165, 250, 0.18), rgba(96, 165, 250, 0.05)); }
        .attendance-day-card.is-holiday::before { background: #60a5fa; }
        .attendance-day-card.is-today {
          border-color: rgba(34, 197, 94, 0.75);
          box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.35), 0 12px 28px rgba(24, 119, 24, 0.2);
        }
        .attendance-day-card:hover {
          transform: translateY(-2px);
          border-color: rgba(255, 255, 255, 0.16);
        }
        .attendance-day-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
        }
        .attendance-day-name {
          color: #f9fafb;
          font-size: 13px;
          font-weight: 800;
        }
        .attendance-day-date {
          color: #9ca3af;
          font-size: 11px;
          font-weight: 700;
          text-align: right;
          white-space: nowrap;
        }
        .attendance-day-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding-top: 10px;
          text-align: center;
        }
        .attendance-day-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px 10px;
          border-radius: 999px;
          background: rgba(34, 197, 94, 0.12);
          color: #bbf7d0;
          font-size: 12px;
          font-weight: 800;
        }
        .attendance-day-card.is-weekend .attendance-day-pill {
          background: rgba(251, 113, 133, 0.16);
          color: #fecdd3;
        }
        .attendance-day-card.is-holiday .attendance-day-pill {
          background: rgba(96, 165, 250, 0.18);
          color: #dbeafe;
        }
        .attendance-day-note {
          color: #dbeafe;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.35;
        }
        @media (max-width: 1100px) {
          .attendance-calendar-weekdays { display: none; }
          .attendance-calendar-grid { grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); }
          .attendance-day-blank { display: none; }
        }
        .attendance-register {
          overflow-x: auto;
          border-radius: 22px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          background:
            radial-gradient(900px 180px at 0% 0%, rgba(24, 119, 24, 0.14), transparent 55%),
            linear-gradient(180deg, #1b2029 0%, #14181f 100%);
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
        }
        .attendance-register-table {
          width: 100%;
          min-width: 860px;
          border-collapse: collapse;
          font-size: 14px;
        }
        .attendance-register-table th {
          padding: 16px 14px;
          color: #9ca3af;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-align: left;
          white-space: nowrap;
          background: rgba(16, 34, 53, 0.72);
          border-bottom: 2px solid #187718;
        }
        .attendance-register-table {
          background: transparent;
          color: #f3f4f6;
          padding: 0;
        }
        .attendance-register-table thead tr th {
          opacity: 1;
          padding: 16px 14px;
          color: #d1d5db;
          font-size: 11px;
          font-weight: 800;
          line-height: 1.3;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-align: left;
          white-space: nowrap;
          background: #102235;
          border-bottom: 2px solid #187718;
        }
        .attendance-register-table tbody tr {
          font-size: 14px;
          font-weight: 600;
          background: transparent;
        }
        .attendance-register-table tbody tr td {
          padding: 14px;
          color: #f9fafb;
          font-weight: 600;
          line-height: 1.4;
          vertical-align: middle;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          background: #171b22;
        }
        .attendance-register-table tbody tr.is-alt td {
          background: #1e242e;
        }
        .attendance-register-table tbody tr.is-today td {
          background: #173024;
          box-shadow: inset 0 -1px 0 rgba(34, 197, 94, 0.28);
        }
        .attendance-register-table tbody tr.is-today td:first-child {
          box-shadow: inset 3px 0 0 #22c55e;
        }
        .attendance-register-table tbody tr.is-weekend td {
          background: #2a1c24;
        }
        .attendance-register-table tbody tr.is-holiday td {
          background: #172433;
        }
        .attendance-register-table tbody tr:hover td {
          filter: brightness(1.08);
        }
        html[data-theme="light"] .attendance-register {
          border-color: var(--home-border, #ced4da);
          background: var(--home-card-bg, #fff);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
        }
        html[data-theme="light"] .attendance-register-table,
        html[data-theme="light"] .attendance-register-table thead tr th,
        html[data-theme="light"] .attendance-register-table tbody tr td,
        html[data-theme="light"] .attendance-register-table tbody tr:nth-child(even) td,
        html[data-theme="light"] .attendance-register-table tbody tr:hover td {
          color: var(--home-heading, #212529);
          background: #fff;
          border-bottom-color: var(--home-border, #e9ecef);
          filter: none;
        }
        html[data-theme="light"] .attendance-register-table thead tr th {
          background: var(--home-card-hover, #f1f3f5);
          color: #495057;
          border-bottom: 2px solid #187718;
        }
        html[data-theme="light"] .attendance-register-table tbody tr.is-alt td,
        html[data-theme="light"] .attendance-register-table tbody tr.is-alt:nth-child(even) td {
          background: #f8f9fa;
        }
        html[data-theme="light"] .attendance-register-table tbody tr.is-today td,
        html[data-theme="light"] .attendance-register-table tbody tr.is-today:nth-child(even) td,
        html[data-theme="light"] .attendance-register-table tbody tr.is-today:hover td {
          background: #ecfdf3;
          box-shadow: inset 0 -1px 0 rgba(22, 163, 74, 0.18);
          filter: none;
        }
        html[data-theme="light"] .attendance-register-table tbody tr.is-weekend td,
        html[data-theme="light"] .attendance-register-table tbody tr.is-weekend:nth-child(even) td,
        html[data-theme="light"] .attendance-register-table tbody tr.is-weekend:hover td {
          background: #fff1f2;
          filter: none;
        }
        html[data-theme="light"] .attendance-register-table tbody tr.is-holiday td,
        html[data-theme="light"] .attendance-register-table tbody tr.is-holiday:nth-child(even) td,
        html[data-theme="light"] .attendance-register-table tbody tr.is-holiday:hover td {
          background: #eff6ff;
          filter: none;
        }
        html[data-theme="light"] .attendance-register-day,
        html[data-theme="light"] .attendance-register-table .is-empty {
          color: #6b7280;
        }
        html[data-theme="light"] .attendance-register-table tr.is-weekend .attendance-register-pill {
          background: rgba(190, 18, 60, 0.1);
          color: #be123c;
        }
        html[data-theme="light"] .attendance-register-table tr.is-holiday .attendance-register-pill,
        html[data-theme="light"] .attendance-register-note {
          background: rgba(29, 78, 216, 0.1);
          color: #1d4ed8;
        }
        html[data-theme="light"] .attendance-month-switch {
          background: var(--home-card-hover, #f1f3f5);
          border-color: var(--home-border, #ced4da);
        }
        html[data-theme="light"] .attendance-month-btn {
          color: var(--home-heading, #212529);
        }
        html[data-theme="light"] .attendance-month-btn:hover {
          background: rgba(0, 0, 0, 0.05);
        }
        html[data-theme="light"] .attendance-summary {
          border-color: var(--home-border, #ced4da);
          background: var(--home-card-bg, #fff);
          box-shadow: none;
        }
        html[data-theme="light"] .attendance-summary h3,
        html[data-theme="light"] .attendance-summary-grid span {
          color: var(--home-muted, #495057);
        }
        html[data-theme="light"] .attendance-summary-grid div {
          border-color: var(--home-border, #ced4da);
          background: var(--home-card-hover, #f8f9fa);
        }
        html[data-theme="light"] .attendance-summary-grid strong {
          color: var(--home-heading, #212529);
        }
        html[data-theme="light"] .attendance-summary-grid strong.is-overtime,
        html[data-theme="light"] .attendance-register-table .is-overtime {
          color: #15803d;
        }
        .attendance-register-date {
          font-weight: 800;
          white-space: nowrap;
        }
        .attendance-register-day {
          color: #d1d5db;
          font-weight: 700;
        }
        .attendance-register-table .is-empty {
          color: #6b7280;
        }
        .attendance-register-table .is-overtime,
        .attendance-summary-grid strong.is-overtime {
          color: #4ade80;
          font-weight: 800;
        }
        .attendance-register-table .is-nowrap {
          white-space: nowrap;
        }
        .attendance-register-closed {
          text-align: left;
        }
        .attendance-register-pill {
          display: inline-flex;
          align-items: center;
          margin-right: 10px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 800;
        }
        .attendance-register-table tr.is-weekend .attendance-register-pill {
          background: rgba(251, 113, 133, 0.16);
          color: #fecdd3;
        }
        .attendance-register-table tr.is-holiday .attendance-register-pill {
          background: rgba(96, 165, 250, 0.18);
          color: #dbeafe;
        }
        .attendance-register-note {
          color: #dbeafe;
          font-weight: 700;
        }
        .attendance-summary {
          margin-top: 16px;
          padding: 16px;
          border-radius: 22px;
          border: 1px solid rgba(255, 255, 255, 0.06);
          background: linear-gradient(180deg, #1b2029 0%, #14181f 100%);
          box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
        }
        .attendance-summary h3 {
          margin: 0 0 12px 0;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #9ca3af;
        }
        .attendance-summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 10px;
        }
        .attendance-summary-grid div {
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.03);
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .attendance-summary-grid span {
          color: #9ca3af;
          font-size: 12px;
          font-weight: 700;
        }
        .attendance-summary-grid strong {
          color: #fff;
          font-size: 18px;
          font-weight: 800;
        }
      `}</style>
    </section>
  );
}

function blockCapturedImageSave(event) {
  event.preventDefault();
}

function AttendanceImage({ src, alt, onOpen }) {
  if (!src) {
    return <span>—</span>;
  }

  return (
    <button
      type="button"
      style={thumbButtonStyle}
      onClick={() => onOpen(src)}
      onContextMenu={blockCapturedImageSave}
      title={`View ${alt}`}
    >
      <ProtectedCapturedImage src={src} alt={alt} style={thumbImageStyle} />
    </button>
  );
}

function ProtectedCapturedImage({ src, alt, style }) {
  return (
    <span style={capturedImageFrameStyle} onContextMenu={blockCapturedImageSave}>
      <img
        src={src}
        alt={alt}
        draggable={false}
        onContextMenu={blockCapturedImageSave}
        onDragStart={blockCapturedImageSave}
        style={{ ...style, ...capturedImageStyle }}
      />
      <span aria-hidden="true" style={capturedImageShieldStyle} onContextMenu={blockCapturedImageSave} />
    </span>
  );
}

const sectionStyle = {
  backgroundColor: "var(--home-card-bg, #ffffff)",
  color: "var(--home-heading, #212529)",
  border: "1px solid var(--home-border, #ced4da)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.06)"
};

const sectionHeaderStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(180px, 1fr) auto minmax(180px, 1fr)",
  alignItems: "center",
  gap: "16px",
  marginBottom: "20px"
};

const headerLeftStyle = {
  minWidth: 0
};

const titleRowStyle = {
  display: "flex",
  alignItems: "baseline",
  gap: "14px",
  flexWrap: "wrap",
  marginBottom: "6px"
};

const employeeNameAboveStyle = {
  margin: 0,
  color: "#4ade80",
  fontSize: "16px",
  fontWeight: 800
};

const sectionTitleStyle = {
  margin: 0,
  color: "var(--home-heading, #212529)",
  fontSize: "24px",
  fontWeight: 800
};

const sectionSubtitleStyle = {
  margin: 0,
  color: "#9ca3af",
  fontSize: "14px"
};

const dutySubtitleStyle = {
  margin: 0,
  fontSize: "14px",
  lineHeight: 1.5
};

const dutyTimingTextStyle = {
  color: "var(--home-heading, #212529)",
  fontWeight: 600
};

const headerMonthNavStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  flexWrap: "wrap"
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
  justifyContent: "flex-end"
};

const formLabelStyle = {
  display: "block",
  marginBottom: "14px",
  color: "var(--home-heading, #fff)",
  fontSize: "13px",
  fontWeight: 700
};

const formInputStyle = {
  display: "block",
  width: "100%",
  marginTop: "8px",
  boxSizing: "border-box",
  border: "1px solid #444",
  borderRadius: "10px",
  backgroundColor: "#1f232b",
  color: "#fff",
  padding: "12px 14px",
  fontSize: "14px",
  outline: "none"
};

const addUserButtonStyle = {
  border: "1px solid #187718",
  background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
  color: "#fff",
  borderRadius: "10px",
  padding: "12px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
  boxShadow: "0 4px 12px rgba(24, 119, 24, 0.3)"
};

const disabledButtonStyle = {
  ...addUserButtonStyle,
  opacity: 0.55,
  cursor: "not-allowed",
  boxShadow: "none"
};

const secondaryButtonStyle = {
  border: "1px solid var(--home-border, #ced4da)",
  backgroundColor: "var(--home-card-bg, #fff)",
  color: "var(--home-heading, #212529)",
  borderRadius: "10px",
  padding: "11px 18px",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer"
};

const monthLabelStyle = {
  minWidth: "160px",
  textAlign: "center",
  fontSize: "18px",
  color: "var(--home-heading, #212529)"
};

const emptyStateStyle = {
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px dashed var(--home-border, #444)",
  borderRadius: "14px",
  color: "var(--home-muted, #aaa)",
  padding: "28px",
  textAlign: "center",
  fontWeight: 600
};

const attendanceMarkStyle = {
  color: "#dc2626",
  fontWeight: 800
};

const thumbButtonStyle = {
  border: "none",
  background: "transparent",
  padding: 0,
  cursor: "pointer"
};

const thumbImageStyle = {
  width: "52px",
  height: "52px",
  objectFit: "cover",
  borderRadius: "8px",
  border: "1px solid #444",
  display: "block"
};

const capturedImageFrameStyle = {
  position: "relative",
  display: "inline-flex"
};

const capturedImageStyle = {
  pointerEvents: "none",
  userSelect: "none",
  WebkitUserDrag: "none"
};

const capturedImageShieldStyle = {
  position: "absolute",
  inset: 0
};

const earlyCheckoutOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "rgba(0, 0, 0, 0.72)"
};

const earlyCheckoutCardStyle = {
  width: "min(100%, 420px)",
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
  color: "var(--home-heading, #fff)"
};

const earlyCheckoutMessageStyle = {
  margin: "0 0 20px 0",
  color: "var(--home-heading, #fff)",
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: 1.5
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  backdropFilter: "blur(4px)"
};

const modalCardStyle = {
  width: "min(100%, 520px)",
  backgroundColor: "var(--home-card-bg, #2a2d35)",
  border: "1px solid var(--home-border, #444)",
  borderRadius: "18px",
  padding: "24px",
  boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
  color: "var(--home-heading, #fff)"
};

const modalHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "16px",
  marginBottom: "20px"
};

const modalTitleStyle = {
  margin: "0 0 6px 0",
  fontSize: "24px",
  fontWeight: 800
};

const modalSubtitleStyle = {
  margin: 0,
  color: "var(--home-muted, #888)",
  fontSize: "14px"
};

const closeButtonStyle = {
  border: "1px solid var(--home-border, #444)",
  backgroundColor: "transparent",
  color: "var(--home-heading, #fff)",
  borderRadius: "8px",
  width: "36px",
  height: "36px",
  cursor: "pointer",
  fontWeight: 800
};

const cameraFrameStyle = {
  position: "relative",
  width: "100%",
  height: "320px",
  overflow: "hidden",
  borderRadius: "14px",
  backgroundColor: "#111",
  marginBottom: "16px",
  border: "1px solid var(--home-border, #444)"
};

const cameraLoadingStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 700,
  backgroundColor: "rgba(0, 0, 0, 0.45)",
  zIndex: 1
};

const cameraVideoStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transform: "scaleX(-1)"
};

const formErrorStyle = {
  color: "#fca5a5",
  backgroundColor: "rgba(127, 29, 29, 0.25)",
  border: "1px solid rgba(248, 113, 113, 0.45)",
  borderRadius: "10px",
  padding: "10px 12px",
  marginBottom: "16px",
  fontSize: "14px",
  fontWeight: 700
};

const modalActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  marginTop: "8px"
};

const previewImageStyle = {
  maxWidth: "90vw",
  maxHeight: "90vh",
  borderRadius: "12px",
  border: "1px solid #444"
};

export default AttendanceTab;
